import { createClient } from '@supabase/supabase-js';
import type { Database } from '../types/supabase';
import type { User } from '../types/auth';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables');
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
    storage: sessionStorage, // Use sessionStorage to ensure session ends when tab is closed
    storageKey: 'araclm-auth-token',
    flowType: 'implicit'
  }
});

// Maximum number of retries for profile fetch
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper to get user profile with retries
async function getUserProfile(userId: string, attempt = 1): Promise<Database['public']['Tables']['user_profiles']['Row']> {
  try {
    const { data: profile, error } = await supabase
      .from('user_profiles')
      .select('*')
      .eq('id', userId)
      .single();

    if (error) {
      throw error;
    }

    if (!profile) {
      throw new Error('Profile not found');
    }

    return profile;
  } catch (error) {
    if (attempt < MAX_RETRIES) {
      await wait(RETRY_DELAY * attempt);
      return getUserProfile(userId, attempt + 1);
    }
    throw error;
  }
}

// Helper to get current user with profile
export async function getCurrentUser(): Promise<User | null> {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    
    if (userError) {
      throw new Error(`Auth error: ${userError.message}`);
    }

    if (!user) {
      return null;
    }

    const profile = await getUserProfile(user.id);

    return {
      id: user.id,
      email: user.email!,
      firstName: profile.first_name,
      lastName: profile.last_name,
      role: profile.role,
      isActive: profile.is_active
    };
  } catch (error) {
    console.error('Error in getCurrentUser:', error);
    throw error; // Let the caller handle the error
  }
}
